import React, { useCallback, useRef, useState, useEffect } from 'react';
import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import QuizOptions from '../shared/QuizOptions';
import QuizOption from '../shared/QuizOption';
import {
  CDN_URL,
  getQuizOptionsData,
  growthBookInstances,
  STORAGE_KEYS,
} from '../../constants';
import { useTranslation } from 'react-i18next';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { trackStyleSelected } from '../../utils/analytics';
import { useDesign } from '../../contexts/DesignContext';
import { useVideoAnimation } from '../../hooks/useVideoAnimation';
import { useGrowthBookValues } from '../../contexts/GrowthBookContext';
import iconHeart from '../../assets/valentinesDay/iconHeart.png';
import iconRose from '../../assets/valentinesDay/iconRose.png';
import timeIcon from '../../assets/time.svg';

const Step0 = ({ onAnswer }) => {
  const { isNewDesign } = useDesign();
  const {
    animationMode,
    shouldPlayOnHoverOrSelectedForStep0,
    getVideoProps,
  } = useVideoAnimation();
  const [selectedOption, setSelectedOption] = useState('Realistic');
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const videoRefs = useRef([]);
  const [videosReady, setVideosReady] = useState(false);
  const isHalloween = window.location.pathname.includes('/halloween');

  const quizOptions = getQuizOptionsData('welcome', selectedOption);

  const growthbook = useGrowthBook();
  const isGrowthBookReady = growthbook?.ready ?? false;

  const { content: value, armResolved } = useGrowthBookValues();

  const [isContentReady, setIsContentReady] = useState(false);

  useEffect(() => {
    if (isGrowthBookReady) {
      setIsContentReady(true);
    } else {
      const timeout = setTimeout(() => {
        setIsContentReady(true);
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [isGrowthBookReady]);

  const baseVideos = [
    `${CDN_URL}/${value['realistic-home-video']}`,
    `${CDN_URL}/${value['anime-home-video']}`,
  ];
  const basePosterImages = [
    `${CDN_URL}/${value['realistic-home-poster']}`,
    `${CDN_URL}/${value['anime-home-poster']}`,
  ];

  const videos = isHalloween
    ? baseVideos.map((url) =>
        url.replace('/createsexfriend/', '/createsexfriend/Halloween/'),
      )
    : baseVideos;

  const posterImages = isHalloween
    ? basePosterImages.map((url) =>
        url.replace('/createsexfriend/', '/createsexfriend/Halloween/'),
      )
    : basePosterImages;

  const playVideo = useCallback((index) => {
    const video = videoRefs.current[index];
    if (video && video.paused) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  }, []);

  const handleOptionHover = useCallback((index, isHovering) => {
    setHoveredIndex(isHovering ? index : null);
    // Step0: both videos always play — no stop on hover
  }, []);

  const handleOptionClick = useCallback((value) => {
    setSelectedOption(value);
    // Step0: both videos always play — no stop on click
  }, []);

  useEffect(() => {
    if (!isContentReady) return;

    const cleanups = [];

    videoRefs.current.forEach((videoRef, index) => {
      if (videoRef) {
        videoRef.muted = true;
        videoRef.loop = true;
        videoRef.playsInline = true;
        // Safari requires these attributes set directly on the element
        videoRef.setAttribute('playsinline', '');
        videoRef.setAttribute('webkit-playsinline', '');
        videoRef.preload = 'auto';
        videoRef.controls = false;
        videoRef.removeAttribute('controls');
        videoRef.removeAttribute('controlsList');

        const tryPlay = () => {
          if (videoRef.paused) {
            videoRef.play().catch(() => {});
          }
        };

        // Safari fires these events in different order depending on cache state
        videoRef.addEventListener('loadedmetadata', tryPlay);
        videoRef.addEventListener('canplay', tryPlay);
        videoRef.addEventListener('loadeddata', tryPlay);

        // On reload with cached video, canplay may never fire — check readyState immediately
        if (videoRef.readyState >= 3) {
          tryPlay();
        }

        cleanups.push(() => {
          videoRef.removeEventListener('loadedmetadata', tryPlay);
          videoRef.removeEventListener('canplay', tryPlay);
          videoRef.removeEventListener('loadeddata', tryPlay);
        });
      }
    });

    return () => cleanups.forEach((fn) => fn());
  }, [isContentReady, animationMode, selectedOption, quizOptions]);

  useEffect(() => {
    if (!isContentReady || !videosReady || !selectedOption) return;

    if (animationMode === 'AUTOMATIC') {
      const defaultIndex = quizOptions.findIndex(
        (opt) => opt.value === selectedOption,
      );

      if (defaultIndex !== -1) {
        const timer = setTimeout(() => {
          playVideo(defaultIndex);
        }, 100);

        return () => clearTimeout(timer);
      }
    }
  }, [
    isContentReady,
    videosReady,
    selectedOption,
    quizOptions,
    playVideo,
    animationMode,
  ]);

  useEffect(() => {
    if (!isContentReady || !videosReady || !selectedOption) return;
    if (!shouldPlayOnHoverOrSelectedForStep0) return;

    const selectedIndex = quizOptions.findIndex(
      (opt) => opt.value === selectedOption,
    );

    if (selectedIndex !== -1) {
      const timer = setTimeout(() => {
        playVideo(selectedIndex);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [
    isContentReady,
    videosReady,
    selectedOption,
    quizOptions,
    playVideo,
    shouldPlayOnHoverOrSelectedForStep0,
  ]);

  const handleVideoCanPlay = useCallback((index) => {
    if (index === 0) {
      setVideosReady(true);
    }
    // Directly attempt play on canPlay — important for Safari where state-driven
    // play (via videosReady → useEffect → setTimeout) can be too slow or blocked
    const video = videoRefs.current[index];
    if (video && video.paused) {
      video.play().catch(() => {});
    }
  }, []);

  // ─── Step0: both videos ALWAYS play, no exceptions ───────────────────────
  //
  // On Step0 both videos must play at all times regardless of animationMode.
  // Three-layer approach:
  //  1. visibilitychange + window.focus  → resume immediately on unlock/tab-switch
  //  2. per-video pause listener         → catches iOS pause even if visibilitychange
  //                                        fires late (after browser already paused)
  //  3. setInterval watchdog (1 s)       → nuclear fallback: if a video is paused
  //                                        or its currentTime stopped advancing
  //                                        (frozen decoder), restart it
  //
  // forcePlay: seek to 0 then play() with 3× retry — seeking is required because
  // play() alone cannot unstick a frozen Safari video decoder.
  useEffect(() => {
    if (!isContentReady) return;

    let resumeTimer = null;
    const lastTimes = {};

    const forcePlay = (video, retriesLeft = 3) => {
      video.currentTime = 0;
      video.play().catch(() => {
        if (retriesLeft > 0) setTimeout(() => forcePlay(video, retriesLeft - 1), 500);
      });
    };

    const playAll = () => {
      videoRefs.current.forEach((video) => {
        if (video) forcePlay(video);
      });
    };

    const schedulePlayAll = () => {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(playAll, 200);
    };

    // Layer 1: visibilitychange
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') schedulePlayAll();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Layer 1b: window.focus — backup for older iOS where visibilitychange is unreliable
    window.addEventListener('focus', schedulePlayAll);

    // Layer 2: per-video pause event while page is hidden
    const pauseCleanups = [];
    videoRefs.current.forEach((video) => {
      if (!video) return;
      const onPause = () => {
        if (document.visibilityState === 'hidden') schedulePlayAll();
      };
      video.addEventListener('pause', onPause);
      pauseCleanups.push(() => video.removeEventListener('pause', onPause));
    });

    // Layer 3: watchdog — catches both paused and frozen-decoder states
    const watchdog = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      videoRefs.current.forEach((video, i) => {
        if (!video) return;
        if (video.paused) {
          forcePlay(video);
        } else if (video.currentTime === lastTimes[i]) {
          // currentTime didn't advance since last check — decoder is frozen
          forcePlay(video);
        }
        lastTimes[i] = video.currentTime;
      });
    }, 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', schedulePlayAll);
      pauseCleanups.forEach((fn) => fn());
      clearInterval(watchdog);
      if (resumeTimer) clearTimeout(resumeTimer);
    };
  }, [isContentReady]);

  const { t } = useTranslation();

  const handleContinue = () => {
    if (selectedOption !== null) {
      trackStyleSelected(selectedOption);

      // Сохраняем resolved URL видео/постера (уже с GB-вариантом) в localStorage,
      // чтобы summary страница не зависела от GrowthBook (паттерн как в step4_5)
      const selectedIndex = quizOptions.findIndex((opt) => opt.value === selectedOption);
      const idx = selectedIndex >= 0 ? selectedIndex : 0;
      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS) || '{}');
        localStorage.setItem(
          STORAGE_KEYS.AI_GIRLFRIEND_SELECTIONS,
          JSON.stringify({
            ...existing,
            welcomeVideoSrc: videos[idx],
            welcomePosterSrc: posterImages[idx],
          }),
        );
      } catch {}

      onAnswer('welcome', selectedOption);
    } else {
      alert(t('select_option'));
    }
  };

  const canContinue = selectedOption;

  // Until the arm resolves, the two cards would render whichever media the flag
  // defaults to — NSFW for a visitor the GB geo rule is about to force to SFW (see
  // GrowthBookContext). Skeletons hold their exact box instead, so the switch does
  // not flash the wrong media and the layout does not jump.
  if (!isContentReady || !armResolved) {
    return (
      <div className="quizContentWrapper quizContentWrapperStep0">
        <QuizContent className="mainStep">
          <QuizTitle>
            {t('create')}<span className="specialText"> {t('your_own')} </span>
            {t('ai_girlfriend')}
          </QuizTitle>
          <div className="step0Wrapper">
            <QuizOptions className="step0-options">
              {[0, 1].map((index) => (
                <div
                  key={index}
                  className="step0-option step0-option-skeleton"
                  aria-hidden="true"
                >
                  <div className="option-image">
                    <div className="step0-skeleton-media" />
                  </div>
                </div>
              ))}
            </QuizOptions>
          </div>
        </QuizContent>
      </div>
    );
  }

  return (
    <div className="quizContentWrapper quizContentWrapperStep0">
      <img
        src={iconHeart}
        alt=""
        className="step0-icon-heart"
        aria-hidden="true"
      />
      <img
        src={iconRose}
        alt=""
        className="step0-icon-rose"
        aria-hidden="true"
      />
      <QuizContent className="mainStep">
        <QuizTitle>
          {t('create')}<span className="specialText"> {t('your_own')} </span>
          {t('ai_girlfriend')}
        </QuizTitle>
        <div className="step0Wrapper">
          <QuizOptions className="step0-options">
            {quizOptions.map((option, index) => {
              const isSelected = selectedOption === option.value;

              return (
                <QuizOption
                  key={option.value}
                  value={option.value}
                  name="welcome"
                  onClick={() => handleOptionClick(option.value, index)}
                  onMouseEnter={() => handleOptionHover(index, true)}
                  onMouseLeave={() => handleOptionHover(index, false)}
                  className={`step0-option ${
                    isSelected
                      ? 'selected'
                      : selectedOption !== null
                      ? 'not-selected'
                      : ''
                  }`}
                >
                  <div className="option-image">
                    <video
                      ref={(el) => (videoRefs.current[index] = el)}
                      className="step0Video"
                      {...getVideoProps()}
                      src={videos[index]}
                      poster={posterImages[index]}
                      preload="auto"
                      alt={option.alt}
                      controls={false}
                      controlsList="nodownload nofullscreen noremoteplayback"
                      playsInline
                      webkit-playsinline=""
                      onCanPlay={() => handleVideoCanPlay(index)}
                    />
                    <div className="step0-image-title">{t(option.text)}</div>
                  </div>
                </QuizOption>
              );
            })}
          </QuizOptions>

          <div className="step0-info-text">
              {isNewDesign ? (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.50288 0C2.92099 0 4.73194e-06 2.91638 0 6.49827C-8.8199e-06 10.0802 2.92098 13 6.50288 13C10.0848 13 13 10.0802 13 6.49827C13 2.91638 10.0848 0 6.50288 0ZM6.50288 1.1815C9.44625 1.1815 11.8185 3.55491 11.8185 6.49827C11.8185 9.44163 9.44626 11.8185 6.50288 11.8185C3.55951 11.8185 1.1815 9.44163 1.1815 6.49827C1.18151 3.55491 3.55952 1.1815 6.50288 1.1815ZM6.49365 2.35493C6.41558 2.35569 6.33844 2.37191 6.26667 2.40266C6.19491 2.43341 6.12995 2.47808 6.07556 2.53408C6.02116 2.59009 5.97841 2.65632 5.94976 2.72895C5.92112 2.80158 5.90715 2.87916 5.90867 2.95722V6.49827C5.90899 6.576 5.92464 6.65291 5.95474 6.72459C5.98483 6.79626 6.02878 6.86129 6.08405 6.91595L8.44706 9.28011C8.50165 9.33664 8.56698 9.38171 8.63921 9.41268C8.71144 9.44364 8.78912 9.45989 8.8677 9.46047C8.94629 9.46104 9.0242 9.44593 9.09688 9.41602C9.16955 9.38611 9.23553 9.34201 9.29095 9.28628C9.34636 9.23056 9.3901 9.16434 9.41961 9.0915C9.44912 9.01866 9.4638 8.94067 9.46279 8.86209C9.46178 8.7835 9.4451 8.70591 9.41374 8.63386C9.38237 8.5618 9.33694 8.49673 9.28011 8.44244L7.09133 6.25366V2.95722C7.09287 2.87809 7.0785 2.79946 7.04907 2.72599C7.01964 2.65253 6.97575 2.58572 6.92 2.52954C6.86425 2.47337 6.79779 2.42896 6.72455 2.39897C6.65131 2.36897 6.57279 2.354 6.49365 2.35493Z"
                    fill="white"
                  />
                </svg>
              ) : (
                <img src={timeIcon} alt="" width={13} height={13} />
              )}
              <span>{t('most_users')} <span className="highlight-text">{t('finish_highlight')}</span> {t('in_30_seconds')}</span>
            </div>

          <button
            disabled={!canContinue}
            className={`continue-button step0 ${
              isNewDesign ? 'new-design-button' : ''
            } ${isNewDesign && selectedOption ? 'option-selected' : ''}`}
            onClick={handleContinue}
          >
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  );
};
//
export default Step0;
